'use client';

import { useState } from 'react';
import Link from 'next/link';

interface WallCardProps {
  href: string;
  /** Resolved (cdnImageUrl) photo URL — the wall pool is photo-only. */
  src: string;
  name: string;
  meta: string | null;
  /** lead = 2x2 signature tile; wide = 2-col 3/2 landscape crop
   *  (Phase 7 mosaic — no orientation metadata exists on photos, so
   *  wide is a crop of the same portrait source). */
  variant?: 'lead' | 'wide' | 'tile';
  className?: string;
}

/**
 * Homepage profile-wall tile. Client component for one reason: a photo
 * that fails to load removes the tile entirely (onError) — the wall is
 * visual proof, and a blank tile is worse than a missing one (Antoine,
 * 2026-07-14; local envs also carry DB photo URLs whose storage
 * objects don't exist). SSR/no-JS renders the tile normally.
 */
const VARIANT_SHAPE = {
  lead: 'col-span-2 row-span-2',
  wide: 'col-span-2 aspect-[3/2]',
  tile: 'aspect-[3/4]',
} as const;

export function WallCard({
  href,
  src,
  name,
  meta,
  variant = 'tile',
  className = '',
}: WallCardProps) {
  const [broken, setBroken] = useState(false);
  const lead = variant === 'lead';

  if (broken) return null;

  return (
    <Link
      href={href}
      className={`group bg-muted relative block overflow-hidden rounded-lg ${VARIANT_SHAPE[variant]} ${className}`}
      data-testid="profile-wall-card"
      aria-label={name}
    >
      {/* Plain <img>: signed/R2 URLs can't go through next/image (see
          entity-card.tsx). Lazy — eager fetches here would compete with
          the hero poster for LCP bandwidth (PageSpeed 2026-07-14). */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={src}
        alt={name}
        loading="lazy"
        decoding="async"
        onError={() => setBroken(true)}
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
      />

      {/* Photo-proof scrim: real photos are uncontrolled, so the caption
          carries its own contrast (a11y review 2026-07-14). */}
      <span
        className="absolute inset-x-0 bottom-0 flex flex-col gap-0.5 p-4 text-white"
        style={{
          background:
            'linear-gradient(transparent, rgba(10,7,9,0.92) 35%, rgba(10,7,9,0.95))',
          textShadow: '0 1px 3px rgba(0,0,0,0.85)',
        }}
      >
        <span
          className={
            lead
              ? 'text-2xl font-bold tracking-tight md:text-4xl'
              : 'text-sm font-semibold'
          }
        >
          {name}
        </span>
        {meta ? (
          <span className={`text-white/70 ${lead ? 'text-sm' : 'text-xs'}`}>
            {meta}
          </span>
        ) : null}
      </span>
    </Link>
  );
}
